/* eslint-disable react-refresh/only-export-components -- context module re-exports hook + provider */
/**
 * Unit-system preference (metric <-> imperial), app-wide.
 *
 * This is a *display* preference, not design data: it is kept in localStorage
 * (per device, not per saved design) so it never enters undo/redo or the
 * persisted design payload. All storage and calculation stay metric — see
 * `src/domain/units/formatUnits.ts`. Imperial is opt-in and only surfaced on
 * the intl market.
 *
 * SSR safety: the server always renders `metric` (the default), and the client
 * hydrates with `metric` too; the stored preference is applied in an effect
 * *after* mount, so there is no hydration mismatch (cf. the SSR hydration-trap
 * notes).
 */
import {
	createContext,
	type ReactNode,
	use,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import type { UnitSystem } from "../domain/units/formatUnits";

const STORAGE_KEY = "bb:unit-system";

interface UnitSystemContextValue {
	unitSystem: UnitSystem;
	setUnitSystem: (unitSystem: UnitSystem) => void;
}

const UnitSystemContext = createContext<UnitSystemContextValue>({
	unitSystem: "metric",
	setUnitSystem: () => {},
});

function readStoredUnitSystem(): UnitSystem | null {
	if (typeof window === "undefined") return null;
	try {
		const stored = window.localStorage.getItem(STORAGE_KEY);
		return stored === "imperial" || stored === "metric" ? stored : null;
	} catch {
		return null;
	}
}

export function UnitSystemProvider({ children }: { children: ReactNode }) {
	const [unitSystem, setUnitSystemState] = useState<UnitSystem>("metric");

	// Apply the stored preference after mount so SSR/first paint stay metric.
	useEffect(() => {
		const stored = readStoredUnitSystem();
		if (stored) setUnitSystemState(stored);
	}, []);

	const setUnitSystem = useCallback((next: UnitSystem) => {
		setUnitSystemState(next);
		if (typeof window === "undefined") return;
		try {
			window.localStorage.setItem(STORAGE_KEY, next);
		} catch {
			// storage unavailable (private mode / quota) — keep the in-memory value
		}
	}, []);

	const value = useMemo(
		() => ({ unitSystem, setUnitSystem }),
		[unitSystem, setUnitSystem],
	);

	return (
		<UnitSystemContext.Provider value={value}>
			{children}
		</UnitSystemContext.Provider>
	);
}

/** Returns the active unit system and a setter. Defaults to metric. */
export function useUnitSystem(): UnitSystemContextValue {
	return use(UnitSystemContext);
}
